// Signed staff contracts (PDF): one or several are read in the browser (who by the DNI in
// «Datos de la persona trabajadora», dates of the third clause, kind, position, working time
// and gross monthly pay), reviewed, and each one is attached to that person's contract. Its
// dates rule: the server attaches it to the contract with the same hire date, corrects the
// only one it overlaps, or creates it (POST /api/professors/:id/contract-documents).
// The file name («NOMBRE 10.09.26 - 15.09.26.pdf») gives the dates when the PDF has no text.
const ContractDocs = {
    _state: null,
    _busy: false,

    canManage() {
        return AuthManager.isAdmin() || AuthManager.isRRHH() || AuthManager.hasModule('personal-laboral', 'manage');
    },

    /** Opens the file picker; `professorId` fixes the person (upload from their ficha). */
    pick(professorId = '') {
        if (!this.canManage()) return;
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'application/pdf,.pdf';
        input.multiple = !professorId;
        input.addEventListener('change', () => { if (input.files.length) this.analyze([...input.files], professorId); });
        input.click();
    },

    /** Some official forms print their text shifted by 29 code points («GHVGH» is «desde»). */
    decode(text) {
        return String(text).replace(/[\x21-\x5d]/g, ch => String.fromCharCode(ch.charCodeAt(0) + 29));
    },

    /** dd/mm/yyyy (or dd.mm.yy) as YYYY-MM-DD, or null. */
    isoDate(text) {
        const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(String(text).trim());
        if (!m) return null;
        const year = m[3].length === 2 ? `20${m[3]}` : m[3];
        const iso = `${year}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
        return StaffPay.validDate(iso) ? iso : null;
    },

    /** Dates in a file name such as «HAIZEA AGUILA 10.09.26 - 15.09-26.pdf». */
    datesFromName(name) {
        const found = [...String(name).matchAll(/(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})/g)].map(m => this.isoDate(`${m[1]}/${m[2]}/${m[3]}`)).filter(Boolean);
        return { startDate: found[0] || null, endDate: found[1] || null };
    },

    /**
     * What the official contract form says, page by page (pdf.js text items). Every field is a
     * best effort and is shown for review before anything is saved.
     */
    readContract(pagesItems, byDni) {
        const pages = pagesItems.map(items => items.filter(i => i.str && i.str.trim()).map(i => ({ text: i.str.trim(), x: i.transform[4], y: i.transform[5] })));
        const all = pages.flat();
        const find = re => { for (const cells of pages) { const hit = cells.find(c => re.test(c.text) || re.test(this.decode(c.text))); if (hit) return { hit, cells }; } return null; };
        const out = { dni: null, startDate: null, endDate: null, contractKind: null, position: null, workingTime: null, grossMonthly: null };

        // The worker: the DNI under «Datos de la persona trabajadora» (the company's signatory comes first).
        const worker = find(/DATOS DE LA PERSONA TRABAJADORA/i);
        const dnisIn = cells => cells.flatMap(c => NominasBatch.findDnis(c.text));
        if (worker) out.dni = dnisIn(worker.cells.filter(c => c.y < worker.hit.y && c.y > worker.hit.y - 45))[0] || null;
        if (!out.dni) out.dni = dnisIn(all).find(d => byDni.has(d)) || null;

        // Kind: the form's title and, if any, its specific clauses («por circunstancias de la producción»).
        const title = all.find(c => /^CONTRATO DE TRABAJO/i.test(c.text));
        const specific = all.find(c => /^CL[AÁ]USULAS ESPEC[IÍ]FICAS/i.test(c.text));
        const lower = text => text.toLowerCase().replace(/\s*\(\d+\)\s*$/, '');
        if (title) out.contractKind = (title.text.charAt(0) + lower(title.text).slice(1) + (specific ? ` · ${lower(specific.text).replace(/^cl[aá]usulas espec[ií]ficas\s*/, '')}` : '')).slice(0, 200);

        // Dates: third clause, «desde … hasta …».
        const third = find(/^TERCERA/i);
        if (third) {
            const dates = third.cells.filter(c => Math.abs(c.y - third.hit.y) <= 8 && this.isoDate(c.text)).sort((a, b) => a.x - b.x).map(c => this.isoDate(c.text));
            [out.startDate, out.endDate] = [dates[0] || null, dates[1] || null];
        }

        // Position: first clause, «prestará sus servicios como …».
        const first = find(/^PRIMERA/i);
        if (first) {
            const job = first.cells.filter(c => c.y > first.hit.y && c.y <= first.hit.y + 8 && c.x > first.hit.x + 120 && /^[A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑ\s/.,-]+$/.test(c.text))[0];
            if (job) out.position = job.text.charAt(0) + job.text.slice(1).toLowerCase();
        }

        // Working time: second clause, full time (hours on its line) or part time (the line below).
        const second = find(/^SEGUNDA/i);
        if (second) {
            const full = second.cells.find(c => /^A tiempo completo/i.test(c.text));
            const hours = cells => cells.find(c => /^\d{1,2}([.,]\d+)?$/.test(c.text));
            const fullHours = full && hours(second.cells.filter(c => Math.abs(c.y - full.y) <= 3 && c.x > full.x));
            const partHours = full && hours(second.cells.filter(c => c.y < full.y - 10 && c.y > full.y - 45));
            if (fullHours) out.workingTime = `A tiempo completo · ${fullHours.text} h/semana`;
            else if (partHours) out.workingTime = `A tiempo parcial · ${partHours.text} h`;
        }

        // Pay: fourth clause, «retribución total de … euros brutos» per month.
        const fourth = find(/^CUARTA/i);
        if (fourth) {
            const row = fourth.cells.filter(c => Math.abs(c.y - fourth.hit.y) <= 4);
            const money = row.find(c => /^\d{1,3}(?:\.\d{3})*,\d{2}$|^\d+,\d{2}$/.test(c.text));
            if (money && row.some(c => /^Mensual/i.test(c.text))) out.grossMonthly = NominasBatch.parseAmount(money.text);
        }
        return out;
    },

    async analyze(files, professorId = '') {
        if (!this.canManage() || this._busy) return;
        const pdfs = files.filter(f => /pdf$/i.test(f.type) || /\.pdf$/i.test(f.name));
        if (!pdfs.length) { Utils.showToast('Elige uno o varios PDF de contratos.', 'error'); return; }
        if (pdfs.some(f => f.size > 20 * 1024 * 1024)) { Utils.showToast('Cada contrato puede ocupar como mucho 20 MB.', 'error'); return; }
        this._busy = true;
        Utils.showToast(`Leyendo ${pdfs.length} ${pdfs.length === 1 ? 'contrato' : 'contratos'}…`, 'info');
        try {
            const { pdfjs } = await NominasBatch.tools();
            const byDni = NominasBatch.professorsByDni();
            const rows = [];
            for (const file of pdfs) {
                let read = { dni: null, startDate: null, endDate: null };
                try {
                    const pdf = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()), isEvalSupported: false }).promise;
                    const pages = [];
                    for (let n = 1; n <= pdf.numPages; n++) pages.push((await (await pdf.getPage(n)).getTextContent()).items);
                    NominasBatch.close(pdf);
                    read = this.readContract(pages, byDni);
                } catch (e) { console.warn('Contract read:', e); }
                const named = this.datesFromName(file.name);
                const person = professorId || (read.dni && byDni.get(read.dni)?.id) || '';
                rows.push({ file, ...read, startDate: read.startDate || named.startDate, endDate: read.endDate || named.endDate,
                    fromName: !read.startDate && !!named.startDate, professorId: person });
            }
            this._state = { rows, fixed: !!professorId };
            this.renderReview();
        } catch (e) {
            console.error('Contracts:', e);
            Utils.showToast('No se pudo leer el PDF.', 'error');
        } finally {
            this._busy = false;
        }
    },

    /** What saving a row will do with that person's contracts (the server decides the same way). */
    preview(row) {
        if (!row.professorId) return { cls: 'pb-skip', text: 'Elige a la persona o déjalo fuera' };
        if (!row.startDate) return { cls: 'pb-warn', text: 'Falta la fecha de alta' };
        if (row.endDate && row.endDate < row.startDate) return { cls: 'pb-warn', text: 'La baja es anterior al alta' };
        const fmt = iso => new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
        const range = c => `${fmt(c.startDate)} – ${c.endDate ? fmt(c.endDate) : 'sin baja'}`;
        const list = StaffPay.contractsOf(DataManager.getProfessor(row.professorId));
        const end = row.endDate || '9999-12-31';
        const overlaps = list.filter(c => c.startDate <= end && (!c.endDate || c.endDate >= row.startDate));
        const same = list.find(c => c.startDate === row.startDate);
        const target = same || (overlaps.length === 1 ? overlaps[0] : null);
        if (!target && overlaps.length > 1) return { cls: 'pb-warn', text: 'Choca con varios contratos de su ficha: corrígelos antes' };
        if (!target) return { cls: 'pb-note', text: 'Se crea este contrato en su ficha' };
        if (target.startDate === row.startDate && (target.endDate || null) === (row.endDate || null))
            return { cls: 'pb-note', text: `Se une a su contrato ${range(target)}${target.documentFilePath ? ' (sustituye el PDF anterior)' : ''}` };
        return { cls: 'pb-warn', text: `Corrige su contrato ${range(target)} a ${range({ startDate: row.startDate, endDate: row.endDate })}` };
    },

    ready(row) {
        const p = this.preview(row);
        return row.professorId && row.startDate && !(row.endDate && row.endDate < row.startDate) && !/^Choca/.test(p.text);
    },

    renderReview() {
        const { rows, fixed } = this._state;
        const professors = (DataManager.getProfessors() || []).slice().sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
        const ready = rows.filter(r => this.ready(r)).length;
        const body = rows.map((r, i) => {
            const p = this.preview(r);
            const info = [r.contractKind, r.position, r.workingTime, r.grossMonthly != null ? `${Utils.formatCurrency(r.grossMonthly)} brutos/mes` : null].filter(Boolean);
            return `<li class="cdr-card">
                <div class="cdr-file"><i class="fas fa-file-pdf" aria-hidden="true"></i> <span>${Utils.sanitizeHTML(r.file.name)}</span>${r.dni && !r.professorId ? ' <span class="pb-warn">DNI sin ficha</span>' : ''}</div>
                <div class="cdr-fields">
                    <label class="cdr-field cdr-person">Persona
                        <select class="form-input" aria-label="Persona del contrato ${i + 1}" ${fixed ? 'disabled' : ''} ${UIEvents.attr("change", function(event,captured){ContractDocs.set(Number(captured[0]),'professorId',this.value);}, [i])}>
                            <option value="">— Dejar fuera —</option>
                            ${professors.map(prof => `<option value="${Utils.sanitizeHTML(prof.id)}" ${prof.id === r.professorId ? 'selected' : ''}>${Utils.sanitizeHTML(`${prof.firstName} ${prof.lastName}`.trim())}</option>`).join('')}
                        </select>
                    </label>
                    <label class="cdr-field">Alta<input class="form-input" type="date" value="${Utils.sanitizeHTML(r.startDate || '')}" aria-label="Alta del contrato ${i + 1}" ${UIEvents.attr("change", function(event,captured){ContractDocs.set(Number(captured[0]),'startDate',this.value);}, [i])}></label>
                    <label class="cdr-field">Baja<input class="form-input" type="date" value="${Utils.sanitizeHTML(r.endDate || '')}" aria-label="Baja del contrato ${i + 1}" ${UIEvents.attr("change", function(event,captured){ContractDocs.set(Number(captured[0]),'endDate',this.value);}, [i])}></label>
                </div>
                <p class="cdr-action ${p.cls}">${Utils.sanitizeHTML(p.text)}</p>
                ${info.length || r.fromName ? `<p class="cdr-info">${Utils.sanitizeHTML([...info, r.fromName ? 'Fechas tomadas del nombre del archivo' : null].filter(Boolean).join(' · '))}</p>` : ''}
            </li>`;
        }).join('');
        ModalManager.show(rows.length === 1 ? 'Revisar contrato' : `Revisar ${rows.length} contratos`, `
            <div class="dnr-proposal professor-form-dnr">
                <p class="pf-hint">Las fechas del contrato mandan: si su ficha tiene otras, se corrigen; si no tiene ese contrato, se crea. El salario del contrato se guarda solo como dato: el coste real sale de las nóminas.</p>
                <ul class="cdr-list">${body}</ul>
                <p class="pf-hint" id="contracts-progress" role="status"></p>
                <div class="modal-sticky-actions">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){ModalManager.closeModal();}, [])}>Cancelar</button>
                    <button type="button" class="btn btn-primary" ${ready ? '' : 'disabled'} ${UIEvents.attr("click", function(event,captured){ContractDocs.upload(this);}, [])}><i class="fas fa-upload"></i> Guardar ${ready === 1 ? 'el contrato' : `${ready} contratos`}</button>
                </div>
            </div>`);
    },

    set(index, key, value) {
        const row = this._state?.rows[index];
        if (!row) return;
        row[key] = value || (key === 'professorId' ? '' : null);
        this.renderReview();
    },

    async upload(button) {
        if (!this.canManage() || button.disabled) return;
        button.disabled = true;
        const rows = this._state.rows.filter(r => this.ready(r)), progress = document.getElementById('contracts-progress');
        const done = { created: 0, updated: 0, attached: 0 }, failed = [];
        for (const [i, row] of rows.entries()) {
            if (progress) progress.textContent = `Guardando ${i + 1} de ${rows.length}: ${row.file.name}…`;
            try {
                const response = await fetch('/upload?folder=staff-contracts', { method: 'POST', headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() }, body: row.file });
                const uploaded = await response.json().catch(() => null);
                if (!response.ok || !uploaded?.success) throw new Error(uploaded?.error || 'No se pudo subir el PDF');
                const saved = await fetch('/api/professors/' + encodeURIComponent(row.professorId) + '/contract-documents', {
                    method: 'POST', headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ startDate: row.startDate, endDate: row.endDate || null, documentFilePath: uploaded.path, documentFileName: row.file.name,
                        contractKind: row.contractKind || null, position: row.position || null, workingTime: row.workingTime || null, grossMonthly: row.grossMonthly ?? null })
                });
                const result = await saved.json().catch(() => null);
                if (!saved.ok || !result?.success) throw new Error(result?.error || 'No se pudo guardar el contrato');
                ProfessorService.cacheProfessor(result.professor);
                done[result.action] = (done[result.action] || 0) + 1;
            } catch (e) {
                failed.push(`${row.file.name} (${e.message})`);
            }
        }
        ModalManager.closeModal();
        const parts = [done.created && `${done.created} creado${done.created === 1 ? '' : 's'}`, done.updated && `${done.updated} con fechas corregidas`, done.attached && `${done.attached} unido${done.attached === 1 ? '' : 's'} a su contrato`].filter(Boolean);
        Utils.showToast(failed.length ? `Guardados: ${parts.join(', ') || 'ninguno'}. Fallaron: ${failed.join('; ')}` : `Contratos guardados: ${parts.join(', ')}.`, failed.length ? 'warning' : 'success');
        const route = (App.currentView || '').startsWith('profesor/') ? App.currentView : null;
        if (route) App.navigate(route);
    },

    /** A contract's PDF in a window over the page. */
    async view(path, title) {
        ModalManager.show(title, '<div id="contract-doc-view" class="doc-viewer-box"><p class="pf-hint">Cargando el contrato…</p></div>');
        await PrivateDownloads.preview('/' + String(path).replace(/^\//, ''), url => {
            const box = document.getElementById('contract-doc-view');
            if (box) PrivateDownloads.renderInlinePreview(box, url, 'application/pdf');
            else URL.revokeObjectURL(url);
        });
    },

    download(path) {
        PrivateDownloads.download('/' + String(path).replace(/^\//, ''));
    }
};

window.ContractDocs = ContractDocs;
